import { FC, useState } from "react";

import { useTheme } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Modal,
  Paper,
  Typography,
} from "@mui/material";

import { Status } from "../types";
import { trackInteraction } from "../../api/interactionBuffer";
import { InformationDisplayer } from "../InformationDisplayer/InformationDisplayer";
import {
  StaticDocumentViewer,
  STATIC_DOC_ESIGN_CONSENT,
} from "../StaticDocumentViewer/StaticDocumentViewer";

const CONSENT_TEXT =
  "I agree to receive and sign documents electronically. I confirm I can access electronic records.";

const SEE_ESIGN_CONSENT_TEXT = "See ESIGN Consent.";

const CONSENT_VERSION = `${CONSENT_TEXT} ${SEE_ESIGN_CONSENT_TEXT}`;

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  height: "80%",
  maxWidth: "832px",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
};

interface EsignConsentProps {
  login: (data: Record<string, unknown>) => Promise<void>;
}

export const EsignConsent: FC<EsignConsentProps> = ({ login }) => {
  const [checked, setChecked] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useTheme();

  const colorHelperText =
    !checked && isDirty ? theme.palette.error.main : "transparent";

  const handleOpenConsent = () => {
    trackInteraction("esign_consent.document_viewed");
    setIsModalOpen(true);
  };

  const handleProceed = async () => {
    trackInteraction("esign_consent.accepted");
    await login({ consentGiven: true, consentVersion: CONSENT_VERSION });
  };

  return (
    <Box style={{ margin: "0 auto" }}>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper elevation={2} style={{ width: "90%", padding: "0 8px 0 8px" }}>
          <InformationDisplayer
            info={{
              status: Status.info,
              mainMessage: "",
            }}
            action={{
              text: "I Agree & Continue",
              clickHandler: handleProceed,
              disabled: !checked,
            }}
            title="Electronic Consent"
            btnVariant="contained"
            children={
              <FormControl
                component="fieldset"
                style={{ margin: "28px 0 4px" }}
                required
                error={!checked && isDirty}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => {
                        setDirty(true);
                        setChecked(!checked);
                        trackInteraction(
                          "field_changed",
                          "esign_consent_checkbox",
                        );
                      }}
                      name="esign-consent"
                    />
                  }
                  label={
                    <Typography variant="body2">{CONSENT_TEXT}</Typography>
                  }
                />
                <Box
                  component="span"
                  onClick={handleOpenConsent}
                  sx={{
                    display: "block",
                    marginLeft: "32px",
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                  }}
                >
                  {SEE_ESIGN_CONSENT_TEXT}
                </Box>
                <FormHelperText style={{ margin: "0", color: colorHelperText }}>
                  You must confirm that you can access electronic records.
                </FormHelperText>
              </FormControl>
            }
          />
        </Paper>
      </Box>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={modalStyle}>
          <StaticDocumentViewer name={STATIC_DOC_ESIGN_CONSENT} />
        </Box>
      </Modal>
    </Box>
  );
};
